import { SimpleOption2 } from "~/components/governance/search/AmbientFacilitiesContext";
import { generalOpOptionExplainations, OperatorOptionExplainationMap, opOptions, OpRuleStates, PickerOptionAmbientDataContextProps, PickValueOption, RuleDisplayKind, RuleDisplayPresetProps, RuleDisplayTree } from "../RuleDisplayPrimitives";
import { DatabaseType, DataInfoTypeResponseDto } from "~/gen/api/encrypt";
import { useGetMainList } from "ra-core";
import { RecPatch } from "~/dataProvider/tools";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useMemo } from "react";


type PickValueKind =
  | 'DatabaseType'
  | 'Database'
  | 'Schema'
  | 'Table'
  | 'Field'
  | 'DataType'
  | 'InfoTypes'
  ;

const defaults = {
  rule: (): RuleDisplayTree => ({
    Kind: 'Equals', 
    Left: {Kind: 'PickValue', Value: 'Database'}, 
    Right: {Kind: 'PickConstant', Value: ''} 
  }),
  opLeft: (): RuleDisplayTree['Left'] => ({ Kind: 'PickValue', Value: 'Database' }),
  opRight: (): RuleDisplayTree['Right'] => ({ Kind: 'PickConstant', Value: '' }),
}

const leftPickeValueOptions: PickValueOption[] = [
  { key: 'DatabaseType', text: 'Database Type', explain: '' },
  { key: 'Database', text: 'Database', explain: '' },
  { key: 'Schema', text: 'Schema', explain: '' },
  { key: 'Table', text: 'Table', explain: '' },
  { key: 'Field', text: 'Field', explain: '' },
  { key: 'DataType', text: 'Data Type', explain: '' },
  { key: 'InfoTypes', text: 'Info Type', explain: '' },
]


const opOptions_exact: SimpleOption2[] = [
  opOptions[0], // equals
  opOptions[3], // equalsAny
]

const dataSrcKind = new Set(['InfoTypes', 'DatabaseType', 'DataType'])
const exactOp = new Set(['Equals', 'EqualsAny'])

const pickerAdjust = {
  adjustOpKind: (r:RuleDisplayTree, v:OpRuleStates):RuleDisplayKind|undefined => {
    if (dataSrcKind.has(r.Value as any) && !exactOp.has(v.composedRule.Kind)) {
      return'EqualsAny'
    }
  },
  adjustOpOptions: (r: RuleDisplayTree): SimpleOption2[]|undefined => {
    return dataSrcKind.has(r.Left?.Value as any) ? opOptions_exact : undefined
  },
}

const PickerOptionAmbientDataController = (): PickerOptionAmbientDataContextProps => {
  const { data: infoTypesData, ids: ids1 } = useGetMainList<DataInfoTypeResponseDto&RecPatch>('settings/encryption/info-types', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY'})

  return useMemo(() => ({
    databaseTypeOptions: Object.keys(DatabaseType).filter(x => !/^\d/.test(x)).sort(),
    dataTypeOptions: [ 'String', 'Number', 'Date' ],
    infoTypesOptions: (ids1 ?? []).map(id => infoTypesData![id].name!).sort((a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : 1),
  }), [ids1, infoTypesData])
}

const getPickerOptionProps = (r: RuleDisplayTree): {
  optionSource: string;
  multiple: boolean;
} | undefined => {
  const optionSource = r.Left?.Kind !== 'PickValue' ? false
    : r.Left.Value === 'DatabaseType' ? 'databaseTypeOptions'
    : r.Left.Value === 'DataType'     ? 'dataTypeOptions'
    : r.Left.Value === 'InfoTypes'    ? 'infoTypesOptions'
    : false
  const multiple = !optionSource ? undefined
    : r.Kind === 'Equals' ? false
    : r.Kind === 'EqualsAny' ? true
    : undefined
  return optionSource && multiple != null ? {optionSource, multiple} : undefined
}

const exampleBase = {
  default: '',
  Matches: 'Enter a valid regular expression.',
  MatchesAny: 'Enter a valid regular expression.',
}

const rightExampleMap = {
  DatabaseType: {...exampleBase,

  },
  Database: {...exampleBase,

  },
  Schema: {...exampleBase,

  },
  Table: {...exampleBase,

  },
  Field: {...exampleBase,

  },
  DataType: {...exampleBase,

  },
  InfoTypes: {...exampleBase,

  },
} as {[k in 'DatabaseType'|'Database'|'Schema'|'Table'|'Field'|'DataType'|'InfoTypes']: Record<string, any>}

const explaination = {
  getOperatorOptionExplainationMap: (r: RuleDisplayTree): OperatorOptionExplainationMap => {
    return generalOpOptionExplainations['general'] as any
  },
  generateRightExample: (composedRule: RuleDisplayTree) => {
    const pv = composedRule.Left?.Value ?? 'Database'
    const op = composedRule.Kind
    const bag = (rightExampleMap as any)[pv]
    return bag[op] ?? bag['default']
  },
}

export const DatabasePreset : RuleDisplayPresetProps = {
  defaults,
  leftPickeValueOptions,
  pickerAdjust,
  rightPickerOptions: {
    useDataSource: PickerOptionAmbientDataController,
    getPickerOptionProps,
  },
  explaination,
}

